import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppContext } from 'context/AppContext';
import { handleResponse } from 'actions/actionHelpers';
import { makeStyles } from 'styles/util';
import routes from 'routes';
import { getBusinessOrPrivateName } from 'helpers/ActorHelper';

import PropTypes from 'prop-types';
import AdminFormWrapper from '../../presentational/AdminFormWrapper';
import AdminFormFields from '../../presentational/AdminFormFields';
import AdminFormButtons from '../../presentational/AdminFormButtons';
import ReadOnlyField from '../../presentational/ReadOnlyField';
import { Link, useHistory } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import { logIn } from 'actions/authentication';
import Form from 'form/Form';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import MaterialUiLink from '@material-ui/core/Link';
import AdminActorSelectorDialog from 'components/adminActorSelector/AdminActorSelectorDialog';
import ShortDate from 'common/ShortDate';
import Percentage from 'common/Percentage';
import Amount from 'common/Amount';
import CheckIcon from '@material-ui/icons/Check';

const useStyles = makeStyles(({ theme }) => ({
    ownerActorName: {
        paddingRight: theme.spacing(1)
    }
}));

const EditStorageSiteForm = ({ storageSite, onSubmit, onCancel, isSubmitting = false }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const { appContext } = useAppContext();

    const [ownerActor, setOwnerActor] = useState({
        id: storageSite.ownerActorId,
        name: storageSite.ownerName
    });
    const [actorSelectorDialogOpen, setActorSelectorDialogOpen] = useState(false);

    const initialValues = {
        ownerActorId: storageSite.ownerActorId
    };

    const handleEditClick = editStorageSite => {
        dispatch(logIn({
            impersonationActorId: editStorageSite.ownerActorId
        }))
        .then(handleResponse(
            () => history.push(routes.account.editStorageSite.replace(':storageSiteId', editStorageSite.id))
        ));
    };

    const handleFormSubmit = formValues => {
        onSubmit({
            ...formValues
        });
    };

    const handleActorSelectorDialogClose = () => {
        setActorSelectorDialogOpen(false);
    };

    // inactivating change owners as of now - need to consider commission rate changes and how to handle existing bookings if we want to allow changing to an owner with a different commission rate
    // const handleChangeOwnerActorClick = () => {
    //     setActorSelectorDialogOpen(true);
    // };

    return (
        <AdminFormWrapper isSubmitting={isSubmitting}>
            <Form
                initialValues={initialValues}
                onSubmit={handleFormSubmit}
            >
                {({ form, handleSubmit }) => {
                    const handleActorSelectorDialogOk = actor => {
                        setOwnerActor(actor);
                        form.change('ownerActorId', actor.id);
                        setActorSelectorDialogOpen(false);
                    };

                    return (
                        <form onSubmit={handleSubmit}>
                            <AdminFormFields>

                                <ReadOnlyField label="Storage site">
                                    <MaterialUiLink
                                        component="button"
                                        onClick={() => handleEditClick(storageSite)}
                                    >
                                        {storageSite.title}
                                    </MaterialUiLink>
                                </ReadOnlyField>

                                <ReadOnlyField label="Owner">
                                    <Link className={classes.ownerActorName} to={routes.admin.actor.replace(':actorId', ownerActor.id)}>{getBusinessOrPrivateName(ownerActor)}</Link>
                                    {
                                        // inactivating change owners as of now - need to consider commission rate changes and how to handle existing bookings if we want to allow changing to an owner with a different commission rate
                                        // <LinkButton onClick={handleChangeOwnerActorClick}>Change</LinkButton>
                                    }
                                </ReadOnlyField>

                                <ReadOnlyField label="Price adjustments">
                                    {
                                        storageSite.priceAdjustments.length === 0 && 'No price adjustments'
                                    }
                                    {
                                        storageSite.priceAdjustments.length > 0 &&
                                        (
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell align="right">Adjustment</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell align="right">Affected bookings</TableCell>
                                                        <TableCell align="center">Bookings only</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {
                                                        storageSite.priceAdjustments.map(priceAdjustment => (
                                                            <TableRow key={priceAdjustment.date}>
                                                                <TableCell>
                                                                    <ShortDate value={priceAdjustment.date}/>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {
                                                                        priceAdjustment.amountDifference !== undefined &&
                                                                        (
                                                                            <Amount value={priceAdjustment.amountDifference} currency={appContext.currency.label}/>
                                                                        )
                                                                    }
                                                                    {
                                                                        priceAdjustment.amountDifference !== undefined &&
                                                                        (
                                                                            <Percentage value={priceAdjustment.percentage} numberOfDecimals={1}/>
                                                                        )
                                                                    }
                                                                </TableCell>
                                                                <TableCell>
                                                                    {priceAdjustment.type}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {priceAdjustment.affectedBookingCount}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {priceAdjustment.appliedToBookingsOnly && <CheckIcon />}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    }
                                                </TableBody>
                                            </Table>
                                        )
                                    }
                                </ReadOnlyField>
                            </AdminFormFields>

                            <AdminFormButtons>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                >
                                    Save
                                </Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="primary"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                            </AdminFormButtons>

                            <AdminActorSelectorDialog
                                ownersOnly
                                title="Select new owner"
                                onOk={handleActorSelectorDialogOk}
                                onClose={handleActorSelectorDialogClose}
                                open={actorSelectorDialogOpen}
                            />
                        </form>
                    );
                }}
            </Form>
        </AdminFormWrapper>
    );
};

EditStorageSiteForm.propTypes = {
    storageSite: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default EditStorageSiteForm;
